import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Card } from '@/components/ui/card';
import SEOHead from '@/components/SEOHead';

export default function PrivacyPolicy() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <SEOHead />
      <div className="w-full max-w-4xl">
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('privacyPolicy.backToHome')}
          </Link>
        </div>
        
        <Card className="bg-card border rounded-lg shadow-lg">
          <div className="p-8 md:p-12">
            <header className="mb-8">
              <h1 className="text-3xl font-bold text-newsletter-primary mb-2">
                {t('privacyPolicy.title')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('privacyPolicy.lastUpdated')}
              </p>
            </header>

            {/* Section 1: General Information */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.section1.title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t('privacyPolicy.section1.intro')}
              </p>
              <div className="space-y-2">
                <p><strong>{t('privacyPolicy.section1.responsible')}</strong></p>
                <p><strong>{t('privacyPolicy.section1.contact')}</strong></p>
              </div>
            </section>

            {/* Section 2: Data We Collect */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.section2.title')}
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-2 text-newsletter-primary">
                    {t('privacyPolicy.section2.accountData.title')}
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>{t('privacyPolicy.section2.accountData.email')}</li>
                    <li>{t('privacyPolicy.section2.accountData.preferences')}</li>
                    <li>{t('privacyPolicy.section2.accountData.subscription')}</li>
                  </ul>
                </div>

                <p className="text-sm italic text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  {t('privacyPolicy.section2.clarification')}
                </p>

                <div>
                  <h3 className="text-lg font-medium mb-2 text-newsletter-primary">
                    {t('privacyPolicy.section2.usageData.title')}
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>{t('privacyPolicy.section2.usageData.interactions')}</li>
                    <li>{t('privacyPolicy.section2.usageData.analytics')}</li>
                    <li>{t('privacyPolicy.section2.usageData.technical')}</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2 text-newsletter-primary">
                    {t('privacyPolicy.section2.cookiesData.title')}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('privacyPolicy.section2.cookiesData.description')}
                  </p>
                </div>

                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  {t('privacyPolicy.section2.thirdParty')}
                </p>
              </div>
            </section>

            {/* Section 3: How We Use Your Data */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.section3.title')}
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-3 text-newsletter-primary">
                    {t('privacyPolicy.section3.legalBases.title')}
                  </h3>
                  <div className="space-y-3">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2 text-newsletter-primary">
                        {t('privacyPolicy.section3.legalBases.consent.title')}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {t('privacyPolicy.section3.legalBases.consent.description')}
                      </p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2 text-newsletter-primary">
                        {t('privacyPolicy.section3.legalBases.contract.title')}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {t('privacyPolicy.section3.legalBases.contract.description')}
                      </p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2 text-newsletter-primary">
                        {t('privacyPolicy.section3.legalBases.legitimate.title')}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {t('privacyPolicy.section3.legalBases.legitimate.description')}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-3 text-newsletter-primary">
                    {t('privacyPolicy.section3.purposes.title')}
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>{t('privacyPolicy.section3.purposes.newsletter')}</li>
                    <li>{t('privacyPolicy.section3.purposes.personalization')}</li>
                    <li>{t('privacyPolicy.section3.purposes.analytics')}</li>
                    <li>{t('privacyPolicy.section3.purposes.support')}</li>
                  </ul>
                </div>

                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  {t('privacyPolicy.section3.consent')}
                </p>
              </div>
            </section>

            {/* Section 4: Data Sharing */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.section4.title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t('privacyPolicy.section4.intro')}
              </p>
              
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-newsletter-primary">
                  {t('privacyPolicy.section4.providers.title')}
                </h3>
                <div className="space-y-2 text-sm">
                  <p className="text-muted-foreground">• {t('privacyPolicy.section4.providers.supabase')}</p>
                  <p className="text-muted-foreground">• {t('privacyPolicy.section4.providers.cloudflare')}</p>
                  <p className="text-muted-foreground">• {t('privacyPolicy.section4.providers.hostinger')}</p>
                  <p className="text-muted-foreground">• {t('privacyPolicy.section4.providers.gtm')}</p>
                  <p className="text-muted-foreground">• {t('privacyPolicy.section4.providers.googleAds')}</p>
                  <p className="text-muted-foreground">• {t('privacyPolicy.section4.providers.lovable')}</p>
                  <p className="text-muted-foreground">• {t('privacyPolicy.section4.providers.ai')}</p>
                  <p className="text-muted-foreground">• {t('privacyPolicy.section4.providers.sendgrid')}</p>
                  <p className="text-muted-foreground">• {t('privacyPolicy.section4.providers.sentry')}</p>
                  <p className="text-muted-foreground">• {t('privacyPolicy.section4.providers.analytics')}</p>
                </div>
                
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  {t('privacyPolicy.section4.safeguards')}
                </p>
              </div>
            </section>

            {/* Section 5: Your Rights */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.section5.title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t('privacyPolicy.section5.intro')}
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-1 text-newsletter-primary">
                    {t('privacyPolicy.section5.rights.access.title')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {t('privacyPolicy.section5.rights.access.description')}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-1 text-newsletter-primary">
                    {t('privacyPolicy.section5.rights.correction.title')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {t('privacyPolicy.section5.rights.correction.description')}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-1 text-newsletter-primary">
                    {t('privacyPolicy.section5.rights.deletion.title')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {t('privacyPolicy.section5.rights.deletion.description')}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-1 text-newsletter-primary">
                    {t('privacyPolicy.section5.rights.portability.title')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {t('privacyPolicy.section5.rights.portability.description')}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-1 text-newsletter-primary">
                    {t('privacyPolicy.section5.rights.consent.title')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {t('privacyPolicy.section5.rights.consent.description')}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-1 text-newsletter-primary">
                    {t('privacyPolicy.section5.rights.objection.title')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {t('privacyPolicy.section5.rights.objection.description')}
                  </p>
                </div>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2 text-newsletter-primary">
                  {t('privacyPolicy.section5.exercise.title')}
                </h4>
                <p className="text-sm text-muted-foreground mb-2">
                  {t('privacyPolicy.section5.exercise.description')}
                </p>
                <p className="text-sm font-medium mb-1 text-newsletter-primary">
                  {t('privacyPolicy.section5.exercise.responseTimes')}
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>{t('privacyPolicy.section5.exercise.brazil')}</li>
                  <li>{t('privacyPolicy.section5.exercise.eea')}</li>
                </ul>
              </div>
            </section>

            {/* Section 6: Changes to our Privacy Policy and Security */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.section6.title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t('privacyPolicy.section6.intro')}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t('privacyPolicy.section6.review')}
              </p>
            </section>

            {/* Section 7: Copyright and Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.section7.title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t('privacyPolicy.section7.intro')}
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t('privacyPolicy.section7.restrictions')}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t('privacyPolicy.section7.usage')}
              </p>
            </section>

            {/* Section 8: Data Security */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.section8.title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t('privacyPolicy.section8.intro')}
              </p>
              
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>{t('privacyPolicy.section8.measures.encryption')}</li>
                <li>{t('privacyPolicy.section8.measures.access')}</li>
                <li>{t('privacyPolicy.section8.measures.monitoring')}</li>
                <li>{t('privacyPolicy.section8.measures.backups')}</li>
                <li>{t('privacyPolicy.section8.measures.incident')}</li>
              </ul>
            </section>

            {/* Section 9: Data Retention */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.section9.title')}
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p className="leading-relaxed">
                  {t('privacyPolicy.section9.analytics')}
                </p>
                <p className="leading-relaxed">
                  {t('privacyPolicy.section9.general')}
                </p>
              </div>
            </section>

            {/* Section 10: Children */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.section10.title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {t('privacyPolicy.section10.content')}
              </p>
            </section>

            {/* Section 11: Cookies */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.section11.title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                {t('privacyPolicy.section11.cookies')}
              </p>
              <Link to="/cookie-policy" className="text-newsletter-primary hover:underline font-medium">
                {t('privacyPolicy.section11.cookieLink')}
              </Link>
            </section>

            {/* Section 12: Contact */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-newsletter-primary">
                {t('privacyPolicy.contact.title')}
              </h2>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('privacyPolicy.contact.intro')}
                </p>
                <div className="space-y-2">
                  <p className="text-sm">
                    <strong>{t('privacyPolicy.contact.email')}</strong>
                  </p>
                  <p className="text-sm">
                    <strong>{t('privacyPolicy.contact.dataController')}</strong>
                  </p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="font-medium text-newsletter-primary mb-3">
                    {t('privacyPolicy.contact.complaint.title')}
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    {t('privacyPolicy.contact.complaint.description')}
                  </p>
                  <ul className="list-none text-muted-foreground space-y-1 mb-3">
                    <li>{t('privacyPolicy.contact.complaint.authorities.brazil')}</li>
                    <li>{t('privacyPolicy.contact.complaint.authorities.eu')}</li>
                    <li>{t('privacyPolicy.contact.complaint.authorities.us')}</li>
                  </ul>
                  <p className="text-muted-foreground text-sm">
                    {t('privacyPolicy.contact.complaint.disclaimer')}
                  </p>
                </div>
              </div>
            </section>

            {/* Footer */}
            <div className="mt-12 pt-8 border-t border-border">
              <div className="text-sm text-muted-foreground space-y-2">
                <p>
                  <strong>{t('privacyPolicy.footer.lastUpdated')}</strong> {t('privacyPolicy.footer.date')}
                </p>
                <p>
                  <strong>{t('privacyPolicy.footer.legalBasis')}</strong> {t('privacyPolicy.footer.legalBasisText')}
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {t('privacyPolicy.footer.ccpaClause')}
                </p>
                <p>
                  <strong>{t('privacyPolicy.footer.questions')}</strong> {t('privacyPolicy.footer.contact')}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}